import {
  FeedbackEvaluationType,
  FeedbackInfoType,
  FeedbackItemType,
  FeedbackReplyStatus,
} from "@/components/HeaderFeedback/types";
import {
  usePostBaobPcComplaintRecordDetail,
  usePostBaobPcComplaintRecordScore,
} from "@/openapi/baob/pc.complaint/api.client.hooks";
import {
  Breadcrumb,
  Button,
  Col,
  Flex,
  Image,
  message,
  Modal,
  Rate,
  Row,
  Spin,
  Tag,
  theme,
} from "antd";
import { useState } from "react";
import { PlayCircleOutlined } from "@ant-design/icons";
import { getVideoPoster } from "@/utils/file";
import { getCurrentUser, getCurrentShopData } from "@/utils/storage";

interface FeedbackDetailProps {
  data: FeedbackItemType | null;
  onBack: () => void;
}
const FeedbackDetail = ({ data, onBack }: FeedbackDetailProps) => {
  const { token: globalToken } = theme.useToken();
  const [score, setScore] = useState<number>(0);
  const [openVideoModal, setOpenVideoModal] = useState<boolean>(false);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const currentShop = getCurrentShopData();

  const {
    isLoading: loading,
    data: detailData,
    refetch: getDetail,
  } = usePostBaobPcComplaintRecordDetail({
    params: {
      complaintId: data?.complaintId,
      supply_id: currentShop?.supplierId,
    },
    query: {
      enabled: !!data?.complaintId,
      select: (data) => {
        return {
          ...data,
          /**
           * app的数据是倒序，前端设计稿要正序
           */
          detailInfo: data?.data?.detailInfo?.sort((a, b) => {
            return a.infoType - b.infoType;
          }),
        };
      },
    },
  });

  const { mutate: submitScore } = usePostBaobPcComplaintRecordScore({
    mutation: {
      onSuccess: () => {
        message.success("感谢您的评分，我们会继续努力！");
        getDetail();
      },
    },
  });

  const handleBack = () => {
    onBack();
  };
  const handleViewVideo = (url: string) => {
    setVideoUrl(url);
    setOpenVideoModal(true);
  };
  const handleScoreChange = (rate: number) => {
    setScore(rate);
  };

  const handleSubmitScore = () => {
    const { userId, userName } = getCurrentUser();
    submitScore({
      complaintId: data?.complaintId,
      score: score,
      supply_id: currentShop?.supplierId,
      user_id: userId,
      user_name: userName,
    });
  };

  return (
    <div className="h-full flex flex-col">
      <Breadcrumb
        className="px-4 py-2"
        items={[
          {
            title: <a onClick={handleBack}>反馈记录列表</a>,
          },
          {
            title: (
              <span style={{ color: globalToken.colorPrimary }}>
                反馈记录详情
              </span>
            ),
          },
        ]}
      />
      <div className="flex-1 relative overflow-y-auto px-4">
        <Spin spinning={loading}>
          {detailData?.data?.detailInfo?.map((item) => {
            return [
              FeedbackInfoType.REPLY,
              FeedbackInfoType.MY_FEEDBACK,
            ].includes(item.infoType) ? (
              <div
                key={item.createdAt}
                className="bg-white p-3 mb-4 rounded hover:shadow-sm"
              >
                <div className="flex flex-row items-center justify-between">
                  <span
                    className="font-bold text-sm"
                    style={{
                      color:
                        item.infoType === FeedbackInfoType.REPLY
                          ? globalToken.colorPrimary
                          : "",
                    }}
                  >
                    {item.title}
                  </span>
                  {item.infoType === FeedbackInfoType.MY_FEEDBACK && (
                    <>
                      {data?.handleStatus === FeedbackReplyStatus.REPLIED ? (
                        <Tag color="error">已回复</Tag>
                      ) : (
                        <Tag color="processing">待回复</Tag>
                      )}
                    </>
                  )}
                </div>
                {!!item.classifyName && (
                  <div className="text-[13px]">
                    <div className="py-3 text-xs leading-none">
                      {item.infoType === FeedbackInfoType.MY_FEEDBACK
                        ? "反馈类型:"
                        : "回复类型:"}
                    </div>
                    <div className="text-[#666666] text-xs leading-none">
                      {item.classifyName}
                    </div>
                  </div>
                )}
                <div className="text-[13px] leading-6">
                  <div className="pt-3 pb-2 leading-none">
                    {item.infoType === FeedbackInfoType.MY_FEEDBACK
                      ? "反馈内容:"
                      : "回复内容:"}
                  </div>
                  <div className="text-[#666666] leading-6 text-xs">
                    {item.content}
                  </div>
                </div>
                {item.videoOrImgs.length > 0 && (
                  <div className="text-[13px]">
                    <div className="pt-2 pb-3 leading-none">
                      {item.infoType === FeedbackInfoType.MY_FEEDBACK
                        ? "相关截图/视频:"
                        : "回复图片:"}
                    </div>
                    <Row gutter={[12, 12]}>
                      {item.videoOrImgs
                        .filter((m) => m.videoUrl)
                        .map((v) => (
                          <Col key={v.videoUrl}>
                            <div
                              className="relative cursor-pointer"
                              onClick={() => handleViewVideo(v.videoUrl)}
                            >
                              <Image
                                src={getVideoPoster(v.imageUrl)}
                                width={72}
                                height={72}
                                className="rounded"
                                preview={false}
                              ></Image>
                              <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center text-2xl">
                                <PlayCircleOutlined style={{ color: "#fff" }} />
                              </div>
                            </div>
                          </Col>
                        ))}
                      <Image.PreviewGroup>
                        {item.videoOrImgs
                          .filter((m) => !m.videoUrl)
                          .map((v) => (
                            <Col key={v.imageUrl}>
                              <Image
                                src={v.imageUrl}
                                width={72}
                                height={72}
                                className="rounded"
                              ></Image>
                            </Col>
                          ))}
                      </Image.PreviewGroup>
                    </Row>
                  </div>
                )}
                <div className="flex flex-row items-center pt-2 pb-3">
                  <div className="text-[13px] leading-none">
                    {item.infoType === FeedbackInfoType.MY_FEEDBACK
                      ? "反馈时间:"
                      : "回复时间:"}
                  </div>
                  <div className="text-[#666666] pl-2 text-xs leading-none">
                    {item.createdAt}
                  </div>
                </div>
              </div>
            ) : (
              <div
                key={item.createdAt}
                className="bg-white p-4 mb-4 rounded flex flex-col items-center hover:shadow-sm"
              >
                <div
                  style={{ color: globalToken.colorPrimary }}
                  className="font-medium text-base"
                >
                  感谢您对一手的回复评分
                </div>
                <Flex gap="middle" className="py-4" align="center">
                  <Rate allowHalf defaultValue={Number(item.handleScore)} />
                  <span>{item.handleScore}星</span>
                </Flex>
              </div>
            );
          })}
          {detailData?.data?.isPopupScore ===
            FeedbackEvaluationType.CAN_EVALUATE && (
            <div className="bg-white p-4 mb-4 rounded flex flex-col items-center hover:shadow-sm">
              <div
                style={{ color: globalToken.colorPrimary }}
                className="font-medium text-base"
              >
                请对一手的处理方式及回复时间进行评分
              </div>
              <Flex gap="middle" className="py-4" align="center">
                <Rate allowHalf value={score} onChange={handleScoreChange} />
                <span>{score}星</span>
              </Flex>
              <Button type="primary" onClick={handleSubmitScore}>
                提交
              </Button>
            </div>
          )}
        </Spin>
      </div>
      <Modal
        open={openVideoModal}
        width="60%"
        footer={null}
        destroyOnClose={true}
        onCancel={() => {
          setOpenVideoModal(false);
        }}
      >
        <video
          controls={true}
          style={{ width: "100%", maxHeight: "780px" }}
          src={videoUrl}
        />
      </Modal>
    </div>
  );
};

export default FeedbackDetail;
